import { Edge, Node } from '@xyflow/react'

export interface RoutePoint {
  x: number
  y: number
  type: 'start' | 'waypoint' | 'end'
}

export interface ConnectionRoute {
  connectionId: number
  pathPoints: RoutePoint[]
  isManual: boolean
}

// Configuration for automatic routing
const ROUTING_CONFIG = {
  minSeparation: 20, // Minimum distance between parallel lines
  cornerRadius: 30,   // Distance from device to first turn
  layerSpacing: 15,   // Vertical spacing between layers
  maxLayers: 10       // Maximum number of parallel layers
}

// Calculate distance between two points
// function distance(p1: { x: number; y: number }, p2: { x: number; y: number }): number {
//   return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))
// }

// Check if two line segments intersect
// function linesIntersect(
//   line1: { x1: number; y1: number; x2: number; y2: number },
//   line2: { x1: number; y1: number; x2: number; y2: number }
// ): boolean {
//   const det = (line1.x2 - line1.x1) * (line2.y2 - line2.y1) - 
//               (line2.x2 - line2.x1) * (line1.y2 - line1.y1)
//   
//   if (Math.abs(det) < 1e-10) return false // Parallel lines
//   
//   const t = ((line2.x1 - line1.x1) * (line2.y2 - line2.y1) - 
//              (line2.x2 - line2.x1) * (line2.y1 - line1.y1)) / det
//   
//   const u = ((line1.x2 - line1.x1) * (line2.y1 - line1.y1) - 
//              (line2.x1 - line1.x1) * (line1.y2 - line1.y1)) / det
//   
//   return t >= 0 && t <= 1 && u >= 0 && u <= 1
// }

// Generate automatic route for a single connection
export function generateAutomaticRoute(
  sourceNode: Node,
  targetNode: Node,
  _sourceHandle: string,
  _targetHandle: string,
  connectionId: number
): ConnectionRoute {
  // Get handle positions (simplified - in real implementation use ReactFlow's getHandlePosition)
  const sourcePos = { x: sourceNode.position.x, y: sourceNode.position.y }
  const targetPos = { x: targetNode.position.x, y: targetNode.position.y }
  
  const startX = sourcePos.x
  const startY = sourcePos.y
  const endX = targetPos.x
  const endY = targetPos.y
  
  // Simple L-shaped route for now
  const pathPoints: RoutePoint[] = [
    { x: startX, y: startY, type: 'start' },
    { x: startX, y: endY, type: 'waypoint' },
    { x: endX, y: endY, type: 'end' }
  ]
  
  return {
    connectionId,
    pathPoints,
    isManual: false
  }
}

// Main function to resolve overlapping routes
export function resolveOverlappingRoutes(
  edges: Edge[],
  nodes: Node[]
): Map<number, ConnectionRoute> {
  const routes = new Map<number, ConnectionRoute>()
  const horizontalSegments: Array<{
    connectionId: number
    y: number
    x1: number
    x2: number
    layer: number
  }> = []
  
  // Generate initial routes for all connections
  edges.forEach(edge => {
    const sourceNode = nodes.find(n => n.id === edge.source)
    const targetNode = nodes.find(n => n.id === edge.target)
    
    if (sourceNode && targetNode) {
      const route = generateAutomaticRoute(
        sourceNode,
        targetNode,
        edge.sourceHandle || '',
        edge.targetHandle || '',
        parseInt(edge.id.replace('connection-', ''))
      )
      routes.set(route.connectionId, route)
      
      // Extract horizontal segments for collision detection
      const horizontalSegs = extractHorizontalSegments(route.pathPoints)
      horizontalSegs.forEach(seg => {
        horizontalSegments.push({
          connectionId: route.connectionId,
          y: seg.y,
          x1: seg.x1,
          x2: seg.x2,
          layer: 0
        })
      })
    }
  })
  
  // Resolve collisions by assigning layers
  resolveCollisions(horizontalSegments)
  
  // Apply layer adjustments to routes
  horizontalSegments.forEach(seg => {
    const route = routes.get(seg.connectionId)
    if (route) {
      route.pathPoints = adjustRouteForLayer(route.pathPoints, seg.layer)
    }
  })
  
  return routes
}

// Extract horizontal segments from path points
function extractHorizontalSegments(pathPoints: RoutePoint[]): Array<{ y: number; x1: number; x2: number }> {
  const segments: Array<{ y: number; x1: number; x2: number }> = []
  
  for (let i = 0; i < pathPoints.length - 1; i++) {
    const current = pathPoints[i]
    const next = pathPoints[i + 1]
    
    // Check if segment is horizontal (within tolerance)
    if (Math.abs(current.y - next.y) < 5) {
      segments.push({
        y: current.y,
        x1: Math.min(current.x, next.x),
        x2: Math.max(current.x, next.x)
      })
    }
  }
  
  return segments
}

// Resolve collisions by assigning layers
function resolveCollisions(segments: Array<{
  connectionId: number
  y: number
  x1: number
  x2: number
  layer: number
}>): void {
  // Sort segments by Y coordinate
  segments.sort((a, b) => a.y - b.y)
  
  // Group segments by similar Y coordinates
  const groups: Array<typeof segments> = []
  let currentGroup: typeof segments = []
  let currentY = segments[0]?.y || 0
  
  segments.forEach(seg => {
    if (Math.abs(seg.y - currentY) < ROUTING_CONFIG.minSeparation) {
      currentGroup.push(seg)
    } else {
      if (currentGroup.length > 0) {
        groups.push([...currentGroup])
      }
      currentGroup = [seg]
      currentY = seg.y
    }
  })
  
  if (currentGroup.length > 0) {
    groups.push(currentGroup)
  }
  
  // Resolve collisions within each group
  groups.forEach(group => {
    resolveGroupCollisions(group)
  })
}

// Resolve collisions within a group of segments
function resolveGroupCollisions(segments: Array<{
  connectionId: number
  y: number
  x1: number
  x2: number
  layer: number
}>): void {
  // Sort by X1 coordinate
  segments.sort((a, b) => a.x1 - b.x1)
  
  // Assign layers to avoid overlaps
  segments.forEach(seg => {
    let layer = 0
    let hasCollision = true
    
    while (hasCollision && layer < ROUTING_CONFIG.maxLayers) {
      hasCollision = false
      
      for (const otherSeg of segments) {
        if (otherSeg.connectionId === seg.connectionId) continue
        
        // Check if segments overlap horizontally
        if (seg.x1 < otherSeg.x2 && seg.x2 > otherSeg.x1) {
          // Check if they're on the same layer
          if (otherSeg.layer === layer) {
            hasCollision = true
            break
          }
        }
      }
      
      if (hasCollision) {
        layer++
      }
    }
    
    seg.layer = layer
  })
}

// Adjust route path points based on assigned layer
function adjustRouteForLayer(pathPoints: RoutePoint[], layer: number): RoutePoint[] {
  if (layer === 0) return pathPoints
  
  const offset = layer * ROUTING_CONFIG.layerSpacing
  const adjustedPoints: RoutePoint[] = []
  
  for (let i = 0; i < pathPoints.length; i++) {
    const point = pathPoints[i]
    
    if (point.type === 'waypoint') {
      // Adjust waypoints vertically
      adjustedPoints.push({
        ...point,
        y: point.y + offset
      })
    } else {
      // Keep start and end points unchanged
      adjustedPoints.push(point)
    }
  }
  
  return adjustedPoints
}

// Convert route to ReactFlow edge with custom path
export function routeToEdge(
  route: ConnectionRoute,
  originalEdge: Edge
): Edge {
  if (route.isManual && route.pathPoints.length > 2) {
    // Use custom path for manual routes
    const pathString = generatePathString(route.pathPoints)
    return {
      ...originalEdge,
      type: 'custom', // Force custom type for manual routes with waypoints
      style: {
        ...originalEdge.style,
        // Custom path will be applied via CSS or custom edge component
      },
      data: {
        ...originalEdge.data,
        customPath: pathString,
        isManualRoute: true
      }
    }
  } else {
    // Use default ReactFlow routing but keep original type
    return {
      ...originalEdge,
      data: {
        ...originalEdge.data,
        isManualRoute: false
      }
    }
  }
}

// Generate SVG path string from route points
function generatePathString(points: RoutePoint[]): string {
  if (points.length < 2) return ''
  
  let path = `M ${points[0].x} ${points[0].y}`
  
  for (let i = 1; i < points.length; i++) {
    path += ` L ${points[i].x} ${points[i].y}`
  }
  
  return path
}
